import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type {
  BulkCreateCustomersFn,
  CreateCustomerFn,
  CreateMemberManuallyFn,
  FetchClientAssignmentFn,
  FetchMemberFn,
  FetchMembersByPublicIdsFn,
  GetCurrentMemberFn,
  SearchMembersFn,
  SearchUsersFn,
} from '@/features/iam'
import type { DocumentsTabViewModelFactory } from '@/features/documents'
import type {
  PromptCreateJobFn,
  UserJobsTabViewModelFactory,
} from '@/features/jobs'
import type { Id } from '@/infra/id'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type { ClientAssignmentViewModel } from '@/features/client-user-management/ClientAssignmentViewModel'
import type { AdvisorClientsTabViewModel } from '@/features/client-user-management/ClientUserDetails/AdvisorClientsTabViewModel'

/**
 * Options for {@link ClientUserManagementFeature}.
 */
export interface ClientUserManagementFeatureOptions {
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  getCurrentMember: GetCurrentMemberFn
  createCustomer: CreateCustomerFn
  bulkCreateCustomers: BulkCreateCustomersFn
  createMemberManually: CreateMemberManuallyFn
  searchMembersByPermission: SearchMembersFn
  fetchMember: FetchMemberFn
  fetchMembersByPublicIds: FetchMembersByPublicIdsFn
  fetchClientAssignment: FetchClientAssignmentFn
  searchUsers: SearchUsersFn
  createDocumentsTabViewModel: PromiseLike<DocumentsTabViewModelFactory>
  promptCreateJob: PromptCreateJobFn
  createUserJobsTabViewModel: PromiseLike<UserJobsTabViewModelFactory>
}

/**
 * Gets the public ID of the user whose client assignment is shown.
 */
export type GetUserPublicIdFn = () => Id

/**
 * Gets the public ID of the advisor whose clients are listed.
 */
export type GetAdvisorUserPublicIdFn = () => Id

/**
 * The user whose client assignment a user details page shows.
 */
export interface ClientAssignmentViewModelOptions {
  getUserPublicId: GetUserPublicIdFn
  /**
   * Whether the user can be a client or an advisor, which only users of type
   * `User` can.
   */
  canHaveAssignment: () => boolean
}

/**
 * Creates the view model for a user's client assignment in the current
 * workspace.
 */
export type ClientAssignmentViewModelFactory = (
  options: ClientAssignmentViewModelOptions,
) => ClientAssignmentViewModel

/**
 * The advisor whose clients a user details page lists.
 */
export interface AdvisorClientsTabViewModelOptions {
  getAdvisorUserPublicId: GetAdvisorUserPublicIdFn
}

/**
 * Creates the view model listing an advisor's clients.
 */
export type AdvisorClientsTabViewModelFactory = (
  options: AdvisorClientsTabViewModelOptions,
) => AdvisorClientsTabViewModel

/**
 * Lets workspace admins search, invite and inspect their clients.
 */
export type ClientUserManagementFeature = ReturnType<
  typeof ClientUserManagementFeature
>

export function ClientUserManagementFeature(
  options: ClientUserManagementFeatureOptions,
) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalClientUserManagementFeature(options),
  )

  const createClientAssignmentViewModel =
    internal.lazy<ClientAssignmentViewModelFactory>(
      (m) => m.createClientAssignmentViewModel,
    )
  const createAdvisorClientsTabViewModel =
    internal.lazy<AdvisorClientsTabViewModelFactory>(
      (m) => m.createAdvisorClientsTabViewModel,
    )

  return {
    // Internal
    internal,

    // Public
    createClientAssignmentViewModel,
    createAdvisorClientsTabViewModel,
  }
}
